import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { fetchSellables } from 'src/models/payment/payment';
import { Identifier } from 'src/types/utilityTypes';

import { BaseSelect, BaseSelectProps, createFormSelect } from './BaseSelect';

interface Sellable {
  id: Identifier;
  type: string;
  name: string;
}

interface SellableSelectProps extends Omit<BaseSelectProps, 'options'> {
  type?: string;
  includeOther?: boolean;
}

export const SellableSelect: React.FC<SellableSelectProps> = ({
  type,
  includeOther = false,
  value,
  onChange,
  ...props
}) => {
  const { data: sellables, isLoading } = useQuery({
    queryKey: ['sellables', type],

    queryFn: () => fetchSellables(type),
  });
  const [groupedSellables, setGroupedSellables] = useState<
    Record<string, Sellable[]>
  >({});

  useEffect(() => {
    if (sellables) {
      const grouped = sellables.reduce((acc, sellable) => {
        let groupType;
        if (sellable.type === 'Group') groupType = 'Group';
        else if (sellable.type === 'Exercisecom::Product')
          groupType = 'Product';
        else if (sellable.type === 'Fbm::Package') groupType = 'Package';
        else if (sellable.type === 'WorkoutPlan') groupType = 'Week Plan';
        else groupType = 'Subscription';

        if (!acc[groupType]) {
          acc[groupType] = [];
        }
        acc[groupType].push(sellable);
        return acc;
      }, {} as Record<string, Sellable[]>);

      setGroupedSellables(grouped);
    }
  }, [sellables]);

  const options = [
    ...(includeOther
      ? [{ id: 'other Other', label: 'Other', value: 'other' }]
      : []),
    ...Object.entries(groupedSellables)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([groupName, items]) => [
      { id: groupName, label: groupName, value: '', disabled: true },
      ...items.map((item) => ({
        id: `${item.id || ''} ${item.type}`,
        label:
          item.type === 'Fbm::Package' ? `Package: ${item.name}` : item.name,
        value: item,
      })),
    ]),
  ];

  return (
    <BaseSelect
      {...props}
      value={value}
      valueOnly={false}
      onChange={onChange}
      options={options}
      loading={isLoading}
      virtualized={true}
    />
  );
};

export const FormSellableSelect = createFormSelect(SellableSelect);
